import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { alpha } from '@mui/material/styles';
import { API_BASE_URL, fetchJson } from './api';
import { AnalyticsInlineState, AnalyticsMetricCard, AnalyticsPageHero, AnalyticsPanelSection, glassSubCardSx } from './analyticsSurface';

function safeString(v) {
    return String(v ?? '').trim();
}

function formatDateTime(iso) {
    try {
        const d = new Date(iso);
        if (!Number.isFinite(d.getTime())) return '';
        return d.toLocaleString();
    } catch {
        return '';
    }
}

function absoluteUrlMaybe(baseUrl, urlOrPath) {
    const raw = safeString(urlOrPath);
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    const base = safeString(baseUrl).replace(/\/+$/, '');
    if (!base) return raw;
    if (raw.startsWith('/')) return `${base}${raw}`;
    return `${base}/${raw}`;
}

function getPriorityChipProps(priority) {
    const value = safeString(priority).toLowerCase();
    if (value === 'high') return { label: 'High', color: 'error' };
    if (value === 'medium') return { label: 'Medium', color: 'warning' };
    if (value === 'low') return { label: 'Low', color: 'success' };
    return null;
}

function IssueReportCard({
    item,
    isAdmin,
    resolvedRetentionDays,
    expanded,
    details,
    onToggle,
    onResolve,
}) {
    const idKey = safeString(item?.id);
    const id = Number(item?.id);
    const createdAt = item?.created_at;
    const payload = item?.payload || {};
    const priority = safeString(payload?.priority).toLowerCase();
    const title = safeString(payload?.title);
    const page = safeString(payload?.page) || 'Unknown page';
    const who = safeString(payload?.reporter_name) || 'Unknown user';
    const email = safeString(payload?.reporter_email);
    const hasImage = Boolean(payload?.has_image);
    const preview = safeString(payload?.description_preview);
    const fullDescription =
        safeString(payload?.description)
        || safeString(payload?.description_full)
        || safeString(payload?.descriptionFull)
        || safeString(payload?.full_description)
        || safeString(payload?.details);

    const resolvedTitle = title || safeString(details?.title) || '';
    const expandedDescription = fullDescription || safeString(details?.description) || preview;

    const imageUrlRaw =
        safeString(payload?.image_url)
        || safeString(payload?.imageUrl)
        || safeString(payload?.screenshot_url)
        || safeString(payload?.screenshotUrl)
        || safeString(payload?.image_path)
        || safeString(payload?.imagePath);

    const imageUrl = absoluteUrlMaybe(API_BASE_URL, imageUrlRaw);
    const imageSrc = imageUrl || safeString(details?.imageObjectUrl);

    const status = safeString(item?.status).toLowerCase() || 'open';
    const isResolved = status === 'resolved' || Boolean(item?.resolved_at);
    const resolvedAt = item?.resolved_at;
    const resolvedComment = safeString(item?.resolved_comment);
    const priorityMeta = getPriorityChipProps(priority);

    let deletionDaysLeft = null;
    if (isResolved) {
        const retention = Number(resolvedRetentionDays);
        const dt = new Date(resolvedAt || createdAt);
        if (Number.isFinite(retention) && retention > 0 && Number.isFinite(dt.getTime())) {
            const ageDays = (Date.now() - dt.getTime()) / 86400000;
            deletionDaysLeft = Math.max(0, Math.ceil(retention - ageDays));
        }
    }

    return (
        <Paper
            key={item?.id}
            id={idKey ? `issue-report-${idKey}` : undefined}
            sx={{
                p: 2,
                borderRadius: 4,
                border: `1px solid ${alpha(isResolved ? '#bbf7d0' : '#fde68a', 0.5)}`,
                background: isResolved
                    ? `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#f0fdf4', 0.78)} 100%)`
                    : `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#fffbeb', 0.78)} 100%)`,
                boxShadow: '0 18px 42px rgba(15, 23, 42, 0.08)'
            }}
        >
            <Stack spacing={1.25}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                            {resolvedTitle || safeString(item?.message) || `Issue report #${Number.isFinite(id) ? id : ''}`}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.45, color: 'text.secondary' }}>
                            {who}{email ? ` • ${email}` : ''} • {page}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center" sx={{ justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                        {Number.isFinite(id) ? <Chip label={`#${id}`} size="small" variant="outlined" /> : null}
                        <Chip label={formatDateTime(createdAt) || 'Unknown time'} size="small" variant="outlined" />
                        {deletionDaysLeft != null ? (
                            <Chip
                                label={`${deletionDaysLeft} day${deletionDaysLeft === 1 ? '' : 's'} until deletion`}
                                size="small"
                                color={deletionDaysLeft <= 1 ? 'error' : (deletionDaysLeft <= 3 ? 'warning' : 'default')}
                                variant="outlined"
                            />
                        ) : null}
                        {priorityMeta ? <Chip label={priorityMeta.label} size="small" color={priorityMeta.color} variant="outlined" /> : null}
                        {hasImage ? <Chip label="Image" size="small" color="secondary" variant="outlined" /> : null}
                        <Chip label={isResolved ? 'Resolved' : 'Open'} size="small" color={isResolved ? 'success' : 'warning'} variant="outlined" />
                        {isAdmin && !isResolved && Number.isFinite(id) ? (
                            <Button size="small" variant="contained" onClick={() => onResolve(item)}>
                                Mark complete
                            </Button>
                        ) : null}
                    </Stack>
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) auto' },
                        gap: 1.25,
                        alignItems: 'start'
                    }}
                >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        <strong>Title:</strong> {resolvedTitle || '—'}
                    </Typography>
                    <Paper sx={{ ...glassSubCardSx, px: 1.25, py: 0.9, minWidth: { lg: 180 } }}>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            Reporter Context
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 800 }}>
                            {page}
                        </Typography>
                    </Paper>
                </Box>

                {(preview || fullDescription || hasImage || resolvedTitle || resolvedComment) ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.25 }}>
                        <IconButton
                            onClick={() => onToggle(idKey)}
                            size="small"
                            sx={{
                                bgcolor: 'transparent !important',
                                color: 'text.secondary',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: 'transparent', color: 'text.primary' }
                            }}
                            aria-label={expanded ? 'Collapse issue report' : 'Expand issue report'}
                            disableRipple
                            disableFocusRipple
                        >
                            <ExpandMoreIcon
                                sx={{
                                    transition: 'transform 160ms ease',
                                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    fontSize: 26,
                                }}
                            />
                        </IconButton>
                    </Box>
                ) : null}

                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 0.5, pt: 2, borderTop: '1px solid', borderColor: alpha('#0f172a', 0.08) }}>
                        {details?.loading ? (
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Loading full details…
                                </Typography>
                            </Stack>
                        ) : null}

                        {details?.error ? (
                            <Alert severity="warning" sx={{ mb: 1.25 }}>
                                {details.error}
                            </Alert>
                        ) : null}

                        <Stack spacing={1.25}>
                            {expandedDescription ? (
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    <strong>Description:</strong> {expandedDescription}
                                </Typography>
                            ) : null}

                            {resolvedComment ? (
                                <Paper sx={{ ...glassSubCardSx }}>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                        Fix Commentary
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.4, whiteSpace: 'pre-wrap' }}>
                                        {resolvedComment}
                                    </Typography>
                                </Paper>
                            ) : null}

                            {resolvedAt ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    <strong>Resolved:</strong> {formatDateTime(resolvedAt) || 'Yes'}
                                </Typography>
                            ) : null}

                            {imageSrc ? (
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.6 }}>
                                        Image
                                    </Typography>
                                    <Box component="a" href={imageSrc} target="_blank" rel="noreferrer" sx={{ display: 'block', textDecoration: 'none' }}>
                                        <Box
                                            component="img"
                                            src={imageSrc}
                                            alt="Issue attachment"
                                            sx={{
                                                width: '100%',
                                                maxHeight: 560,
                                                objectFit: 'contain',
                                                borderRadius: 2.5,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                bgcolor: 'background.default'
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ) : hasImage ? (
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                    Image was attached, but no stored image URL is available (check the email for the attachment).
                                </Typography>
                            ) : null}
                        </Stack>
                    </Box>
                </Collapse>
            </Stack>
        </Paper>
    );
}

export default function IssueReports({ isAdmin = false } = {}) {
    const [openItems, setOpenItems] = useState([]);
    const [resolvedItems, setResolvedItems] = useState([]);
    const [resolvedRetentionDays, setResolvedRetentionDays] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [expandedIds, setExpandedIds] = useState([]);
    const [detailsById, setDetailsById] = useState({});
    const lastLoadedRef = useRef(0);

    const focusHandledRef = useRef(false);

    const objectUrlsRef = useRef(new Set());

    const [resolveOpen, setResolveOpen] = useState(false);
    const [resolveItem, setResolveItem] = useState(null);
    const [resolveCommentary, setResolveCommentary] = useState('');
    const [resolveSaving, setResolveSaving] = useState(false);
    const [resolveError, setResolveError] = useState('');
    const [resolveSuccess, setResolveSuccess] = useState('');

    function isResolvedItem(it) {
        const status = safeString(it?.status).toLowerCase();
        return status === 'resolved' || Boolean(it?.resolved_at);
    }

    async function load() {
        const now = Date.now();
        if (loading) return;
        if (now - lastLoadedRef.current < 300) return;
        lastLoadedRef.current = now;

        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            if (!token) throw new Error('Not authenticated');

            // Non-admin users can only open a specific issue report via notification deep-link.
            if (!isAdmin) {
                let issueId = '';
                try {
                    const qs = new URLSearchParams(window.location.search || '');
                    issueId = safeString(qs.get('issueReportId') || qs.get('issue_report_id') || qs.get('issue_report') || qs.get('issue'));
                } catch {
                    issueId = '';
                }

                if (!issueId) {
                    setOpenItems([]);
                    setResolvedItems([]);
                    setResolvedRetentionDays(null);
                    return;
                }

                const headers = { Authorization: `Bearer ${token}` };
                const json = await fetchJson(`${API_BASE_URL}/issue-reports/${encodeURIComponent(issueId)}`, { method: 'GET', headers });
                const item = json?.item || null;
                if (!item) throw new Error('Issue report not found');

                const retentionRaw = json?.resolved_retention_days;
                const retentionNum = Number(retentionRaw);
                setResolvedRetentionDays(Number.isFinite(retentionNum) && retentionNum > 0 ? Math.floor(retentionNum) : null);

                if (isResolvedItem(item)) {
                    setOpenItems([]);
                    setResolvedItems([item]);
                } else {
                    setOpenItems([item]);
                    setResolvedItems([]);
                }
                return;
            }

            const base = new URLSearchParams();
            base.set('limit', '200');

            const openQs = new URLSearchParams(base.toString());
            openQs.set('status', 'open');
            const resolvedQs = new URLSearchParams(base.toString());
            resolvedQs.set('status', 'resolved');

            const headers = { Authorization: `Bearer ${token}` };
            const [openJson, resolvedJson, settingsJson] = await Promise.all([
                fetchJson(`${API_BASE_URL}/admin/issue-reports?${openQs.toString()}`, { method: 'GET', headers }),
                fetchJson(`${API_BASE_URL}/admin/issue-reports?${resolvedQs.toString()}`, { method: 'GET', headers }),
                fetchJson(`${API_BASE_URL}/admin/settings/issue-report`, { method: 'GET', headers }).catch(() => ({}))
            ]);

            const retentionRaw = settingsJson?.resolved_retention_days;
            const retentionNum = Number(retentionRaw);
            setResolvedRetentionDays(Number.isFinite(retentionNum) && retentionNum > 0 ? Math.floor(retentionNum) : null);

            const openList = Array.isArray(openJson?.items) ? openJson.items : [];
            const resolvedList = Array.isArray(resolvedJson?.items) ? resolvedJson.items : [];

            // Ensure the UI always shows two definitively separate lists.
            // We take the union of both API lists, de-dupe by id, and then split by resolved status.
            const merged = [...openList, ...resolvedList];
            const seenIds = new Set();
            const openFinal = [];
            const resolvedFinal = [];

            for (const it of merged) {
                const id = it?.id;
                if (!id || seenIds.has(id)) continue;
                seenIds.add(id);
                if (isResolvedItem(it)) resolvedFinal.push(it);
                else openFinal.push(it);
            }

            const byIdDesc = (a, b) => {
                const ai = Number(a?.id);
                const bi = Number(b?.id);
                if (!Number.isFinite(ai) && !Number.isFinite(bi)) return 0;
                if (!Number.isFinite(ai)) return 1;
                if (!Number.isFinite(bi)) return -1;
                return bi - ai;
            };
            openFinal.sort(byIdDesc);
            resolvedFinal.sort(byIdDesc);

            setOpenItems(openFinal);
            setResolvedItems(resolvedFinal);

            try {
                window.dispatchEvent(new CustomEvent('monsurv:issue-reports-updated'));
            } catch {
                // ignore
            }
        } catch (e) {
            setError(e?.message || 'Failed to load issue reports');
        } finally {
            setLoading(false);
        }
    }

    const getAuthHeader = useCallback(() => {
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            return token ? { Authorization: `Bearer ${token}` } : {};
        } catch {
            return {};
        }
    }, []);

    const loadDetailsIfNeeded = useCallback(async (idKey) => {
        const key = safeString(idKey);
        if (!key) return;

        setDetailsById((prev) => {
            if (prev?.[key]?.loading) return prev;
            if (prev?.[key]?.loaded) return prev;
            return { ...(prev || {}), [key]: { loading: true, loaded: false, error: '', description: '', imageObjectUrl: '' } };
        });

        try {
            const headers = getAuthHeader();
            if (!headers.Authorization) throw new Error('Not authenticated');

            const details = await fetchJson(`${API_BASE_URL}/admin/issue-reports/${encodeURIComponent(key)}/details`, {
                method: 'GET',
                headers
            });

            const title = safeString(details?.title);
            const desc = safeString(details?.description);
            const hasImg = Boolean(details?.has_image);

            let imageObjectUrl = '';
            if (hasImg) {
                try {
                    const resp = await fetch(`${API_BASE_URL}/admin/issue-reports/${encodeURIComponent(key)}/image`, {
                        method: 'GET',
                        headers
                    });
                    if (resp.ok) {
                        const blob = await resp.blob();
                        const url = URL.createObjectURL(blob);
                        objectUrlsRef.current.add(url);
                        imageObjectUrl = url;
                    }
                } catch {
                    // ignore image fetch errors; show fallback text
                }
            }

            setDetailsById((prev) => ({
                ...(prev || {}),
                [key]: {
                    loading: false,
                    loaded: true,
                    error: '',
                    title,
                    description: desc,
                    imageObjectUrl
                }
            }));
        } catch (e) {
            setDetailsById((prev) => ({
                ...(prev || {}),
                [key]: {
                    loading: false,
                    loaded: true,
                    error: safeString(e?.message) || 'Failed to load issue report details',
                    title: '',
                    description: '',
                    imageObjectUrl: ''
                }
            }));
        }
    }, [getAuthHeader]);

    useEffect(() => {
        if (focusHandledRef.current) return;

        let issueId = '';
        let qs = null;
        try {
            qs = new URLSearchParams(window.location.search || '');
            issueId = safeString(qs.get('issueReportId') || qs.get('issue_report_id') || qs.get('issue_report') || qs.get('issue'));
        } catch {
            issueId = '';
        }

        if (!issueId) return;
        if (loading) return;

        const all = [...(Array.isArray(openItems) ? openItems : []), ...(Array.isArray(resolvedItems) ? resolvedItems : [])];
        const found = all.find((it) => safeString(it?.id) === issueId);

        focusHandledRef.current = true;

        if (found) {
            setExpandedIds((prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.includes(issueId) ? list : [...list, issueId];
            });
            if (isAdmin) void loadDetailsIfNeeded(issueId);
            setTimeout(() => {
                try {
                    const el = document.getElementById(`issue-report-${issueId}`);
                    if (el && typeof el.scrollIntoView === 'function') {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                } catch {
                    // ignore
                }
            }, 80);
        } else {
            setError(`Issue report #${issueId} was not found (it may have been deleted).`);
        }

        // Clear the focus param after first use so navigation returns to normal.
        try {
            if (qs) {
                qs.delete('issueReportId');
                qs.delete('issue_report_id');
                qs.delete('issue_report');
                qs.delete('issue');
                const nextQs = qs.toString();
                const next = `${window.location.pathname}${nextQs ? `?${nextQs}` : ''}${window.location.hash || ''}`;
                window.history.replaceState({}, document.title, next);
            }
        } catch {
            // ignore
        }
    }, [loading, openItems, resolvedItems, isAdmin, loadDetailsIfNeeded]);

    function openResolveModal(item) {
        setResolveSuccess('');
        setResolveError('');
        setResolveItem(item || null);
        setResolveCommentary('');
        setResolveOpen(true);
    }

    function closeResolveModal() {
        if (resolveSaving) return;
        setResolveOpen(false);
        setResolveItem(null);
        setResolveCommentary('');
        setResolveError('');
    }

    async function submitResolve() {
        if (resolveSaving) return;
        const id = Number(resolveItem?.id);
        if (!Number.isFinite(id)) return;
        const comment = safeString(resolveCommentary);
        if (!comment) {
            setResolveError('Fix commentary is required.');
            return;
        }

        setResolveSaving(true);
        setResolveError('');
        setResolveSuccess('');
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            if (!token) throw new Error('Not authenticated');

            const json = await fetchJson(`${API_BASE_URL}/admin/issue-reports/${encodeURIComponent(String(id))}/resolve`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ commentary: comment })
            });

            const item = json?.item || null;
            if (!item) throw new Error('Resolve failed');

            // Remove resolved issue immediately from Open, and show it under Resolved.
            setOpenItems((prev) => (Array.isArray(prev) ? prev : []).filter((x) => (Number(x?.id) !== id)));
            setResolvedItems((prev) => {
                const list = Array.isArray(prev) ? prev : [];
                const without = list.filter((x) => (Number(x?.id) !== id));
                return [item, ...without];
            });
            setExpandedIds((prev) => {
                const key = safeString(id);
                const list = Array.isArray(prev) ? prev : [];
                return key ? list.filter((x) => x !== key) : list;
            });
            setDetailsById((prev) => {
                const key = safeString(id);
                if (!key) return prev;
                const next = { ...(prev || {}) };
                delete next[key];
                return next;
            });
            setResolveSuccess('Marked as complete. The reporter will be notified.');
            setResolveOpen(false);

            try {
                window.dispatchEvent(new CustomEvent('monsurv:issue-reports-updated'));
            } catch {
                // ignore
            }
        } catch (e) {
            setResolveError(e?.message || 'Failed to mark issue as complete');
        } finally {
            setResolveSaving(false);
        }
    }

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function matchesQuery(n, q) {
        const msg = safeString(n?.message).toLowerCase();
        const page = safeString(n?.payload?.page).toLowerCase();
        const who = safeString(n?.payload?.reporter_name).toLowerCase();
        const email = safeString(n?.payload?.reporter_email).toLowerCase();
        const preview = safeString(n?.payload?.description_preview).toLowerCase();
        const title = safeString(n?.payload?.title).toLowerCase();
        const resolvedComment = safeString(n?.resolved_comment).toLowerCase();
        return msg.includes(q) || page.includes(q) || who.includes(q) || email.includes(q) || preview.includes(q) || title.includes(q) || resolvedComment.includes(q);
    }

    const filteredOpen = useMemo(() => {
        const q = safeString(query).toLowerCase();
        const list = Array.isArray(openItems) ? openItems : [];
        if (!q) return list;
        return list.filter((n) => matchesQuery(n, q));
    }, [openItems, query]);

    const filteredResolved = useMemo(() => {
        const q = safeString(query).toLowerCase();
        const list = Array.isArray(resolvedItems) ? resolvedItems : [];
        if (!q) return list;
        return list.filter((n) => matchesQuery(n, q));
    }, [resolvedItems, query]);

    const metrics = useMemo(() => {
        const allItems = [...(Array.isArray(openItems) ? openItems : []), ...(Array.isArray(resolvedItems) ? resolvedItems : [])];
        const highPriorityOpen = (Array.isArray(openItems) ? openItems : []).filter((item) => safeString(item?.payload?.priority).toLowerCase() === 'high').length;
        const imageCount = allItems.filter((item) => Boolean(item?.payload?.has_image)).length;
        return {
            openCount: Array.isArray(openItems) ? openItems.length : 0,
            resolvedCount: Array.isArray(resolvedItems) ? resolvedItems.length : 0,
            highPriorityOpen,
            imageCount,
            shownCount: filteredOpen.length + filteredResolved.length,
        };
    }, [filteredOpen.length, filteredResolved.length, openItems, resolvedItems]);

    function toggleExpanded(idKey) {
        const key = safeString(idKey);
        if (!key) return;
        setExpandedIds((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const next = list.includes(key) ? list.filter((x) => x !== key) : [...list, key];
            if (isAdmin && !list.includes(key)) void loadDetailsIfNeeded(key);
            return next;
        });
    }

    useEffect(() => {
        const urls = objectUrlsRef.current;
        return () => {
            try {
                for (const url of urls) {
                    try { URL.revokeObjectURL(url); } catch { }
                }
            } catch {
                // ignore
            }
        };
    }, []);

    function openIssueReportingSettings() {
        try {
            const qs = new URLSearchParams(window.location.search || '');
            qs.set('view', 'settings');
            qs.set('section', 'issue_reporting');
            window.location.assign(`${window.location.pathname}?${qs.toString()}`);
        } catch {
            window.location.assign(`${window.location.pathname}?view=settings&section=issue_reporting`);
        }
    }

    return (
        <Box sx={{ display: 'grid', gap: 2 }}>
            <Dialog open={resolveOpen} onClose={closeResolveModal} maxWidth="sm" fullWidth>
                <DialogTitle>Mark issue as complete</DialogTitle>
                <DialogContent sx={{ pt: 1.5 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                        Add a short note describing the fix applied. This will be sent to the user who reported the issue.
                    </Typography>
                    <TextField
                        value={resolveCommentary}
                        onChange={(e) => setResolveCommentary(e.target.value)}
                        label="Fix commentary"
                        placeholder="What was changed to resolve this issue?"
                        fullWidth
                        multiline
                        minRows={4}
                        disabled={resolveSaving}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                alignItems: 'flex-start'
                            },
                            '& .MuiOutlinedInput-inputMultiline': {
                                padding: '12px 14px'
                            }
                        }}
                    />
                    {resolveError ? <Alert severity="error" sx={{ mt: 2 }}>{resolveError}</Alert> : null}
                    {resolveSuccess ? <Alert severity="success" sx={{ mt: 2 }}>{resolveSuccess}</Alert> : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeResolveModal} disabled={resolveSaving}>Cancel</Button>
                    <Button variant="contained" onClick={submitResolve} disabled={resolveSaving}>
                        {resolveSaving ? 'Saving…' : 'Mark complete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <AnalyticsPageHero
                eyebrow={isAdmin ? 'Support Queue' : 'Issue Status'}
                title={isAdmin ? 'Issue triage and resolution' : 'Issue status and evidence'}
                actions={
                    <>
                        {isAdmin ? (
                            <Button variant="outlined" startIcon={<SettingsIcon />} onClick={openIssueReportingSettings}>
                                Issue Reporting Settings
                            </Button>
                        ) : null}
                        <Button variant="contained" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
                            {loading ? 'Refreshing…' : 'Refresh'}
                        </Button>
                    </>
                }
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${isAdmin ? 4 : 3}, minmax(0, 1fr))` }, gap: 2 }}>
                <AnalyticsMetricCard
                    icon={<BugReportRoundedIcon fontSize="small" />}
                    label="Open Issues"
                    value={metrics.openCount}
                    caption="Current unresolved issue reports waiting for review or completion."
                    accent="linear-gradient(135deg, #b45309, #ef4444)"
                />
                <AnalyticsMetricCard
                    icon={<CheckCircleRoundedIcon fontSize="small" />}
                    label="Resolved"
                    value={metrics.resolvedCount}
                    caption="Completed issues still visible during the configured retention period."
                    accent="linear-gradient(135deg, #15803d, #22c55e)"
                />
                <AnalyticsMetricCard
                    icon={<ImageRoundedIcon fontSize="small" />}
                    label="With Evidence"
                    value={metrics.imageCount}
                    caption="Issue reports with uploaded screenshots or supporting image attachments."
                    accent="linear-gradient(135deg, #7c3aed, #2563eb)"
                />
                {isAdmin ? (
                    <AnalyticsMetricCard
                        icon={<WarningAmberRoundedIcon fontSize="small" />}
                        label="High Priority Open"
                        value={metrics.highPriorityOpen}
                        caption="Open items currently marked high priority and likely to need fastest attention."
                        accent="linear-gradient(135deg, #f97316, #dc2626)"
                    />
                ) : null}
            </Box>

            <AnalyticsPanelSection
                eyebrow="Search & Control"
                title="Triage controls"
                right={<Chip label={`${metrics.shownCount} shown`} variant="outlined" sx={{ fontWeight: 800, backgroundColor: alpha('#ffffff', 0.56) }} />}
            >
                <Stack spacing={1.5}>
                    <TextField
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        label="Search"
                        placeholder="Name, email, page, text…"
                        size="small"
                        sx={{ minWidth: 260, maxWidth: 680 }}
                    />
                    {error ? <Alert severity="error">{error}</Alert> : null}
                </Stack>
            </AnalyticsPanelSection>

            {loading && !openItems.length && !resolvedItems.length ? (
                <AnalyticsPanelSection eyebrow="Loading" title="Issue reports">
                    <AnalyticsInlineState kind="loading" message="Loading issue reports..." compact />
                </AnalyticsPanelSection>
            ) : null}

            <AnalyticsPanelSection
                eyebrow="Open Queue"
                title="Open issues"
                subtitle="Marking an item complete removes it from this queue immediately and moves it to the resolved retention list below."
                right={<Chip label={`${filteredOpen.length} shown`} variant="outlined" sx={{ fontWeight: 800, backgroundColor: alpha('#ffffff', 0.56) }} />}
            >
                {!loading && filteredOpen.length === 0 ? (
                    <AnalyticsInlineState
                        kind="empty"
                        title="No open issue reports"
                        message="Open reports will appear here when users submit new issues."
                    />
                ) : (
                    <Stack spacing={1.5}>
                        {filteredOpen.map((item) => {
                            const idKey = safeString(item?.id);
                            return (
                                <IssueReportCard
                                    key={item?.id}
                                    item={item}
                                    isAdmin={isAdmin}
                                    resolvedRetentionDays={resolvedRetentionDays}
                                    expanded={idKey ? expandedIds.includes(idKey) : false}
                                    details={idKey ? detailsById?.[idKey] : null}
                                    onToggle={toggleExpanded}
                                    onResolve={openResolveModal}
                                />
                            );
                        })}
                    </Stack>
                )}
            </AnalyticsPanelSection>

            <AnalyticsPanelSection
                eyebrow="Resolved Archive"
                title={`Resolved issues${resolvedRetentionDays ? ` (pending deletion after ${resolvedRetentionDays} days)` : ' (pending deletion)'}`}
                subtitle={`Includes fix commentary. Resolved items are automatically deleted after ${resolvedRetentionDays ? `${resolvedRetentionDays} days` : 'a short retention period'}.`}
                right={<Chip label={`${filteredResolved.length} shown`} variant="outlined" sx={{ fontWeight: 800, backgroundColor: alpha('#ffffff', 0.56) }} />}
            >
                {!loading && filteredResolved.length === 0 ? (
                    <AnalyticsInlineState
                        kind="empty"
                        title="No resolved issue reports"
                        message="Resolved reports will appear here until they pass retention deletion."
                    />
                ) : (
                    <Stack spacing={1.5}>
                        {filteredResolved.map((item) => {
                            const idKey = safeString(item?.id);
                            return (
                                <IssueReportCard
                                    key={item?.id}
                                    item={item}
                                    isAdmin={isAdmin}
                                    resolvedRetentionDays={resolvedRetentionDays}
                                    expanded={idKey ? expandedIds.includes(idKey) : false}
                                    details={idKey ? detailsById?.[idKey] : null}
                                    onToggle={toggleExpanded}
                                    onResolve={openResolveModal}
                                />
                            );
                        })}
                    </Stack>
                )}
            </AnalyticsPanelSection>
        </Box>
    );
}
